import type { GuideStep } from "./guide";
import { BramPx, buildBubble, buildTip, buildWelcome, type BubbleParts, type TipParts, type WelcomeParts } from "./guideWidgets";
import { moodFor } from "./bram/bramBrain";
import { mountBram, type BramHandle } from "./bram/bramView";

/**
 * A step's card with its buttons wired (split from guideOverlay.ts): the welcome card, the tip or the speech bubble,
 * and the captain mounted in it. The overlay owns the spotlight, the layout and the lifecycle.
 */

export interface CardHandlers {
  cta(alt: boolean): void;
  close(): void;
  skip(): void;
  /** The Skip chip's confirm opened (true) or closed. */
  confirming(on: boolean): void;
}

export interface StepCard {
  readonly bubble: BubbleParts | null;
  readonly tip: TipParts | null;
  readonly welcome: WelcomeParts | null;
  /** What goes into the overlay root after the spotlight pieces. */
  readonly el: HTMLElement;
}

export function buildStepCard(step: GuideStep, skip: false | "walkthrough" | "hints", h: CardHandlers): StepCard {
  if (step.kind === "welcome") {
    const welcome = buildWelcome(step);
    welcome.cta.addEventListener("click", () => h.cta(false));
    welcome.alt?.addEventListener("click", () => h.cta(true));
    return { bubble: null, tip: null, welcome, el: welcome.root };
  }
  if (step.kind === "tip") {
    const tip = buildTip(step);
    tip.close.addEventListener("click", (e) => { e.stopPropagation(); h.close(); });
    return { bubble: null, tip, welcome: null, el: tip.tip };
  }
  const b = buildBubble(step, skip);
  const confirming = (on: boolean) => { h.confirming(on); b.confirm!.hidden = !on; b.wrap.classList.toggle("confirming", on); };
  b.chip?.addEventListener("click", (e) => { e.stopPropagation(); confirming(true); b.keep!.focus(); });
  b.keep?.addEventListener("click", (e) => { e.stopPropagation(); confirming(false); });
  b.skip?.addEventListener("click", (e) => { e.stopPropagation(); h.confirming(false); h.skip(); });
  return { bubble: b, tip: null, welcome: null, el: b.wrap };
}

/**
 * The captain in the card, once it is in the document (he only draws where he can be seen): the welcome card's
 * figure, the bubble's bust, and the tip's smaller bust (live too since WP9 O3: the stills read flat beside him).
 */
export function mountStepBram(step: GuideStep, c: StepCard): BramHandle | null {
  const mood = moodFor(step);
  if (c.welcome) return mountBram(c.welcome.hero, { framing: "hero", mood, kind: step.kind, cssPx: BramPx.Hero });
  if (c.bubble) return mountBram(c.bubble.bust, { framing: "bust", mood, kind: step.kind, cssPx: BramPx.Bust });
  if (c.tip) return mountBram(c.tip.bust, { framing: "bust", mood, kind: step.kind, cssPx: BramPx.Tip });
  return null;
}
